import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";

export const Gallery = ({ photos }) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  if (!photos.length) return null;
  const cells = photos.slice(0, 5);

  return (
    <section id="galeria" data-testid="gallery-section" className="mx-auto max-w-7xl px-5 py-10 sm:px-8 md:py-16">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-display text-4xl text-ink sm:text-5xl">
            A casa, <em className="text-clay">de perto</em>.
          </h2>
          <button
            data-testid="show-all-photos-btn"
            onClick={() => { setIndex(0); setOpen(true); }}
            className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm text-ink transition-[background-color,color] duration-300 hover:bg-ink hover:text-cream"
          >
            <Images size={16} /> Mostrar todas as fotos
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:grid-rows-2">
          {cells.map((photo, i) => (
            <motion.button
              key={photo.id || i}
              data-testid={`gallery-cell-${i}`}
              onClick={() => { setIndex(i); setOpen(true); }}
              whileHover="hover"
              className={`group relative overflow-hidden rounded-2xl ${
                i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"
              }`}
            >
              <motion.img
                variants={{ hover: { scale: 1.05 } }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                src={photo.url}
                alt={photo.original_filename || `Foto ${i + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/15" />
            </motion.button>
          ))}
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4"
            onClick={() => setOpen(false)}
          >
            <button
              data-testid="lightbox-close-btn"
              onClick={() => setOpen(false)}
              className="absolute right-5 top-5 rounded-full bg-cream/10 p-3 text-cream transition-colors duration-300 hover:bg-clay"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev-btn"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 rounded-full bg-cream/10 p-3 text-cream transition-colors duration-300 hover:bg-clay"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.img
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              src={photos[index].url}
              alt={`Foto ${index + 1}`}
              className="max-h-[82vh] max-w-full rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="lightbox-next-btn"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 rounded-full bg-cream/10 p-3 text-cream transition-colors duration-300 hover:bg-clay"
            >
              <ChevronRight size={22} />
            </button>
            <span className="absolute bottom-6 text-sm tracking-widest text-cream/70" data-testid="lightbox-counter">
              {index + 1} / {photos.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
